import { useState, useEffect, useCallback, useRef } from 'react';
import { streamDaemonRuns, streamEmployeeRuns } from '../generated/api/sdk.gen.js';
import { sseFetch } from '../generated/api-client.js';

export interface DaemonRunEvent {
  event: 'started' | 'step_completed' | 'finding' | 'completed' | 'failed' | 'update';
  runId: number;
  [key: string]: unknown;
}

export function useDaemonRunStream(daemonId: number | null, apiBasePath: string) {
  const [events, setEvents] = useState<DaemonRunEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const connect = useCallback(() => {
    if (!daemonId) return;

    const controller = new AbortController();
    abortRef.current = controller;

    // The two scopes are two generated stream operations (`/admin/daemons` and `/workforce/employees`).
    const open = apiBasePath === '/admin/daemons' ? streamDaemonRuns : streamEmployeeRuns;
    const connection = open({
      path: { id: String(daemonId) },
      signal: controller.signal,
      sseMaxRetryAttempts: 1,
      fetch: sseFetch,
      onSseEvent: ({ data, event }) => {
        if (typeof data !== 'object' || data === null) return;
        const payload = data as Record<string, unknown>;
        const evt = {
          event: event || payload.event || 'update',
          ...payload,
        } as DaemonRunEvent;
        setEvents((prev) => [...prev, evt]);
      },
    });

    setConnected(true);
    connection
      .then(async ({ stream }) => {
        // The events are delivered through `onSseEvent`; iterating drives the connection until it ends or aborts.
        for await (const _chunk of stream) {
          /* consumed by onSseEvent */
        }
      })
      .catch(() => {
        /* aborted or network error */
      })
      .finally(() => {
        setConnected(false);
      });
  }, [daemonId, apiBasePath]);

  const disconnect = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setConnected(false);
  }, []);

  const reset = useCallback(() => {
    setEvents([]);
  }, []);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  return { events, connected, connect, disconnect, reset };
}
